import { DOCUMENT } from '@angular/common';
import {
  Component,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  Renderer2
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Meta } from '@angular/platform-browser';
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationError,
  Router,
  RouterOutlet
} from '@angular/router';
import { LoadingBarModule } from '@ngx-loading-bar/core';
import { LoadingBarHttpClientModule } from '@ngx-loading-bar/http-client';
import { filter, map, tap, switchMap, distinctUntilChanged } from 'rxjs';
import { NgSelectConfig } from '@ng-select/ng-select';
import { flatMap, isObject, uniq } from 'lodash-es';
import { NgxPermissionsService } from 'ngx-permissions';

import { UserRole } from '~core/models/auth.models';
import { OrganizationUser } from '~core/models/organization.models';
import { Permission } from '~core/models/roles.models';
import { BaseComponent } from './~shared/abstract-components/base.component';
import { IconService } from './~shared/services/icon.service';
import { ProfileService } from './~shared/services/profile.service';
import { OnboardingGuideComponent } from './~shared/components/modals/onboarding-guide/onboarding-guide.component';
import { PREFERENCE_KEYS } from '~core/constants/global.constants';
import { InactivityCheckService } from './~shared/services/inactivity-check.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [LoadingBarModule, LoadingBarHttpClientModule, RouterOutlet]
})
export class AppComponent extends BaseComponent implements OnInit, OnDestroy {
  private _iconService = inject(IconService);
  private _ngSelectConfig = inject(NgSelectConfig);
  protected _permissionsService = inject(NgxPermissionsService);
  private _profileService = inject(ProfileService);
  private _renderer = inject(Renderer2);
  private _document = inject(DOCUMENT);
  private _inactivityCheckService = inject(InactivityCheckService);

  @HostListener('window:online')
  onOnlineEvent(): void {
    this.setOnlineStatus(true);
  }

  @HostListener('window:offline')
  onOfflineEvent(): void {
    this.setOnlineStatus(false);
  }

  constructor(
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private _metaService: Meta
  ) {
    super();
  }

  ngOnInit() {
    this.addMetaTags();
    if (this._isPlatformBrowser) {
      this._syncEnvironmentThemeClass();
      this.setOnlineStatus();
      this.listenToRouteParamsChanges();
      this.listenToNavigationErrors();
      this._listenToInactivityCheckState();
    }
    this._iconService.loadIcons();
    this._initNgSelectConfig();
    this.listenToRoleChanges();
  }

  ngOnDestroy(): void {
    if (!this._isPlatformBrowser) {
      return;
    }
    this._inactivityCheckService.stopMonitoring();
    this._renderer.removeClass(this._document.body, 'sandbox-theme');
  }

  listenToRouteParamsChanges(): void {
    this._router.events
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter(event => event instanceof NavigationEnd),
        map(() => this._activatedRoute),
        map(route => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        tap(activatedRoute => {
          const { snapshot } = activatedRoute;
          const params = this._getActivatedRouteParams(activatedRoute);
          const routeData = this._globalFacade.routeData;

          this._globalFacade.setRouteData({
            params: params,
            queryParams: snapshot.queryParams,
            currentUrl: this._router.url,
            currentUrlWithoutParams: this._router.url.split('?')[0],
            previousUrl: routeData?.currentUrl || '/',
            data: snapshot.data
          });

          // this._listenToCurrentUser();
          this._checkOnboardingStatus();
          this.setDynamicTags();
        })
      )
      .subscribe();
  }

  listenToNavigationErrors(): void {
    this._router.events
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter(event => event instanceof NavigationError),
        tap(event => {
          const navigationError = event as NavigationError;
          const errorMessage = this._getErrorMessage(navigationError.error);
          const isChunkLoadError =
            errorMessage.includes('ChunkLoadError') ||
            errorMessage.includes('Loading chunk') ||
            errorMessage.includes(
              'Failed to fetch dynamically imported module'
            ) ||
            errorMessage.includes('Importing a module script failed');

          if (!isChunkLoadError) {
            return;
          }

          const reloadKey = 'app:reloaded-after-chunk-error';
          const hasReloaded = sessionStorage.getItem(reloadKey);

          this._loggerService.warn(
            'Detected chunk load error during navigation',
            {
              url: navigationError.url,
              message: errorMessage
            }
          );

          if (hasReloaded) {
            return;
          }

          sessionStorage.setItem(reloadKey, 'true');
          window.location.reload();
        })
      )
      .subscribe();
  }

  private _listenToCurrentUser(): void {
    this._authFacade.currentUser$
      .pipe(
        filter(Boolean),
        takeUntilDestroyed(this._destroyRef),
        tap(currentUser => {
          if (!currentUser.organization.onboardingCompleted) {
            this._router.navigateByUrl('/onboarding');
          }
        })
      )
      .subscribe();
  }

  setOnlineStatus(status?: boolean): void {
    this._globalFacade.setOnlineStatus(
      status === undefined ? navigator.onLine : status
    );
  }

  addMetaTags(): void {
    this._metaService.updateTag({
      property: 'og:url',
      content: this.environment.baseUrl
    });
    this._metaService.updateTag({
      property: 'og:type',
      content: 'website'
    });
    this._metaService.updateTag({
      property: 'og:description',
      content: 'app'
    });
    this._metaService.updateTag({
      property: 'og:site_name',
      content: 'app'
    });
    this._metaService.updateTag({
      property: 'og:image',
      content: `${this.environment.baseUrl}/assets/icons/logo.png`
    });

    // this._metaService.updateTag({
    //   property: 'twitter:image',
    //   content: `${environment.baseUrl}/assets/icons/logo.png`
    // });
    // this._metaService.updateTag({
    //   property: 'twitter:card',
    //   content: 'summary_large_image'
    // });
    // this._metaService.updateTag({
    //   property: 'twitter:site',
    //   content: '@username'
    // });
    // this._metaService.updateTag({
    //   property: 'twitter:creator',
    //   content: '@username'
    // });
    // this._metaService.updateTag({
    //   property: 'twitter:title',
    //   content: 'app'
    // });
    // this._metaService.updateTag({
    //   property: 'twitter:description',
    //   content: 'app'
    // });
  }

  setDynamicTags(): void {
    //  TODO: set dynamic tags
  }

  listenToRoleChanges(): void {
    this._authFacade.currentUser$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap({
          next: user => {
            if (
              user?.roles.includes(UserRole.ROLE_SUPER_ADMIN) ||
              user?.roles.includes(UserRole.ROLE_ADMIN)
            ) {
              this._permissionsService.loadPermissions(
                Object.values(Permission).filter(permission =>
                  permission.endsWith(':read')
                )
              );
              return;
            }

            if (user?.roles.includes(UserRole.ROLE_ORGANIZATION_ADMIN)) {
              this._permissionsService.loadPermissions(
                Object.values(Permission)
              );
              return;
            }

            if (!user?.organizationScopes) return;

            if (Array.isArray(user.organizationScopes)) {
              const permissions = uniq(
                flatMap(
                  user.organizationScopes.map(scope => scope.role.permissions)
                )
              );
              this._permissionsService.loadPermissions(permissions);
              return;
            }

            if (isObject(user.organizationScopes)) {
              const organizationScopes =
                user.organizationScopes as unknown as Record<
                  string,
                  OrganizationUser
                >;
              const permissions = uniq(
                flatMap(
                  Object.values(organizationScopes).map(
                    scope => scope.role.permissions
                  )
                )
              );
              this._permissionsService.loadPermissions(permissions);
              return;
            }
          }
        })
      )
      .subscribe();
  }

  private _syncEnvironmentThemeClass(): void {
    this._renderer.removeClass(this._document.body, 'sandbox-theme');
    if (this.isSandboxEnvironment) {
      this._renderer.addClass(this._document.body, 'sandbox-theme');
    }
  }

  private _initNgSelectConfig(): void {
    this._ngSelectConfig.notFoundText = 'Aucun élément trouvé';
  }

  private _getActivatedRouteParams(
    route: ActivatedRoute
  ): Record<string, string> {
    const allParams = { ...route.snapshot.params };
    let currentRoute = route.parent;

    // Traverse up and accumulate params from parents
    while (currentRoute) {
      Object.assign(allParams, currentRoute.snapshot.params);
      currentRoute = currentRoute.parent;
    }

    return allParams;
  }

  private _getErrorMessage(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }

    if (typeof error === 'string') {
      return error;
    }

    return '';
  }

  private _checkOnboardingStatus(): void {
    this._authFacade.currentUser$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter(user => !!user),
        distinctUntilChanged((prev, curr) => prev.id === curr.id),
        switchMap(user =>
          this._profileService
            .readPreference()
            .pipe(map(prefs => ({ prefs, user })))
        ),
        tap(({ prefs, user }) => {
          const currentUrl = this._router.url.split('?')[0];
          const isFeatureRoute = ![
            '/auth',
            '/verification',
            '/onboarding',
            '/cgu'
          ].some(prefix => currentUrl.startsWith(prefix));

          if (
            !user.roles.includes(UserRole.ROLE_SUPER_ADMIN) &&
            !user.roles.includes(UserRole.ROLE_ADMIN) &&
            !prefs[PREFERENCE_KEYS.ONBOARDING_CONSOLE_COMPLETED] &&
            user.organization.onboardingCompleted &&
            isFeatureRoute
          ) {
            this._dialogService.open({
              component: OnboardingGuideComponent,
              config: {
                panelClass: ['mat-dialog-custom'],
                disableClose: true,
                maxWidth: '800px'
              }
            });
          }
        })
      )
      .subscribe();
  }

  private _listenToInactivityCheckState(): void {
    this._authFacade.currentUser$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        map(user => !!user),
        distinctUntilChanged(),
        tap(isAuthenticated => {
          if (isAuthenticated) {
            this._inactivityCheckService.startMonitoring();
            return;
          }
          this._inactivityCheckService.stopMonitoring();
        })
      )
      .subscribe();
  }
}
